import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Sidebar from "../Sidebar";
import { Badge } from "../ui/Badge";
import {
  Search,
  Eye,
  X,
  Flag,
  ChevronDown,
  ChevronRight,
  ArrowLeftRight,
  Layers,
} from "lucide-react";
import {
  buildGraph,
  collectProviders,
  collectVisitTypes,
  collectFlags,
  matchRoute,
  explainMatch,
  blockKeyForRoute,
  resolveProviderName,
} from "./buildGraph";
import { nodeTypes, ResourceLegend, BlockToggleContext } from "./nodes";
import TableView from "./TableView";
import ViewChooser from "./ViewChooser";
import { collectAllProviders } from "./buildTable";
import { templatePath } from "../Templates/templateUrls";
import {
  TABLE_VIEW,
  TREE_VIEW,
  doctorWorkflowsPath,
  isWorkflowView,
  workflowsChooserPath,
  workflowsViewPath,
} from "./workflowsUrls";
import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";
// The config holds real provider names and internal UUIDs, so it is fetched
// from the same authenticated endpoint /workflows uses, never bundled.

// Visit-type sentinel. `null` means nothing picked (no matching, no zoom);
// this means a real visit whose type no route lists.
const ANY_VISIT_TYPE = "__any__";

const FIT_VIEW_OPTIONS = { padding: 0.15, minZoom: 0.1, maxZoom: 1.75, duration: 200 };
const FOCUS_VIEW_OPTIONS = { padding: 0.25, minZoom: 0.1, maxZoom: 1.1, duration: 350 };
const FOCUS_LIST_ZOOM = 0.9;

// Collects a block node plus all of its descendants by walking the edges.
function collectSubtreeIds(rootId, edges) {
  const ids = new Set([rootId]);
  let added = true;
  while (added) {
    added = false;
    for (const edge of edges) {
      if (ids.has(edge.source) && !ids.has(edge.target)) {
        ids.add(edge.target);
        added = true;
      }
    }
  }
  return ids;
}

// Fits the whole graph when the selection changes, but zooms into a
// just-expanded block's subtree so its routes stay readable.
function ViewController({ nodes, edges, focusBlockKey, fitSignature }) {
  const { fitView, setCenter } = useReactFlow();
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (focusBlockKey) {
        const ids = collectSubtreeIds(focusBlockKey, edges);
        const focusNodes = nodes.filter((n) => ids.has(n.id));
        if (focusNodes.some((n) => n.type === "listNode")) {
          const block = nodes.find((n) => n.id === focusBlockKey);
          if (block) {
            const width = block.style?.width || 230;
            setCenter(block.position.x + width / 2, block.position.y + 300, {
              zoom: FOCUS_LIST_ZOOM,
              duration: FOCUS_VIEW_OPTIONS.duration,
            });
            return;
          }
        }
        if (focusNodes.length >= 1) {
          fitView({ ...FOCUS_VIEW_OPTIONS, nodes: focusNodes.map((n) => ({ id: n.id })) });
          return;
        }
      }
      const overviewNodes = nodes
        .filter((n) => n.type !== "listNode")
        .map((n) => ({ id: n.id }));
      fitView(
        overviewNodes.length
          ? { ...FIT_VIEW_OPTIONS, nodes: overviewNodes }
          : FIT_VIEW_OPTIONS
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [focusBlockKey, fitSignature, nodes, edges, fitView, setCenter]);
  return null;
}

function FlowCanvas({
  workflow,
  expandedKeys,
  collapsedKeys,
  selectedProviderName,
  highlightBlockKeys,
  matchedRouteIndex,
  focusBlockKey,
  fitSignature,
  onToggleBlock,
}) {
  const { nodes, edges } = useMemo(
    () =>
      buildGraph(workflow, {
        expandedKeys,
        collapsedKeys,
        selectedProviderName,
        highlightBlockKeys,
        matchedRouteIndex,
      }),
    [
      workflow,
      expandedKeys,
      collapsedKeys,
      selectedProviderName,
      highlightBlockKeys,
      matchedRouteIndex,
    ]
  );

  const onNodeClick = useCallback(
    (event, node) => {
      if (node.type !== "groupNode") return;
      onToggleBlock(node.id);
    },
    [onToggleBlock]
  );

  return (
    <BlockToggleContext.Provider value={onToggleBlock}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesFocusable={false}
        elementsSelectable={false}
        panOnScroll
        minZoom={0.1}
        maxZoom={1.75}
        proOptions={{ hideAttribution: true }}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
      >
        <ViewController
          nodes={nodes}
          edges={edges}
          focusBlockKey={focusBlockKey}
          fitSignature={fitSignature}
        />
        <Background gap={16} size={1.5} color="#d1d5db" variant="dots" />
        <Controls showInteractive={false} position="bottom-left" />
      </ReactFlow>
    </BlockToggleContext.Provider>
  );
}

const WorkflowsVisualization = ({ setAuth, setUserOrg }) => {
  // Workflows are fetched from the authenticated S3-backed endpoint (read-only).
  const [workflowsList, setWorkflowsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [specialtySearch, setSpecialtySearch] = useState("");
  // Visit conditions: one patient type + a set of flags.
  const [selectedVisitType, setSelectedVisitType] = useState(null);
  const [selectedFlags, setSelectedFlags] = useState(() => new Set());
  // Explicit user expand/collapse overrides, tracked per specialty.
  const [expandedBySpecialty, setExpandedBySpecialty] = useState({});
  const [collapsedBySpecialty, setCollapsedBySpecialty] = useState({});
  const [manualFocusKey, setManualFocusKey] = useState(null);
  const [specialtyOpen, setSpecialtyOpen] = useState(false);
  const specialtyRef = useRef(null);
  const specialtyInputRef = useRef(null);
  const [flagsOpen, setFlagsOpen] = useState(false);
  const flagsRef = useRef(null);
  const [traceOpen, setTraceOpen] = useState(false);

  // --- URL state -----------------------------------------------------------
  // View and doctor are path segments so the page is linkable. Specialty,
  // visit type, and flags stay local — they filter a view rather than naming
  // what the page is about.
  const { view: viewParam, doctorId } = useParams();
  const navigate = useNavigate();

  // No segment means no view picked yet, so the chooser. An unrecognized one
  // (a slug from the old URL scheme, say) is a dead link and goes there too.
  const viewMode = isWorkflowView(viewParam) ? viewParam : null;
  const unknownView = Boolean(viewParam) && !viewMode;

  const allProviders = useMemo(
    () => collectAllProviders(workflowsList),
    [workflowsList]
  );

  // Doctors are addressed by user_id, so this is one match or none; the table
  // reports a miss rather than silently showing someone else.
  const tableProvider = useMemo(
    () => (doctorId ? allProviders.find((p) => p.id === doctorId) || null : null),
    [allProviders, doctorId]
  );

  // Tree specialty picker — starts empty so the page asks before drawing a
  // graph. A pinned doctor's only specialty is implied, same as the table.
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  // Table specialty picker for multi-specialty doctors. Single-specialty
  // doctors get theirs implied.
  const [tableSpecialtyPick, setTableSpecialtyPick] = useState(null);

  const tableSpecialty = useMemo(() => {
    if (!tableProvider) return null;
    if (tableProvider.specialties.length === 1) return tableProvider.specialties[0];
    return tableProvider.specialties.includes(tableSpecialtyPick)
      ? tableSpecialtyPick
      : null;
  }, [tableProvider, tableSpecialtyPick]);

  useEffect(() => {
    if (!workflowsList?.length) return;
    setSelectedSpecialty((prev) => {
      if (tableProvider) {
        if (prev && tableProvider.specialties.includes(prev)) return prev;
        if (tableProvider.specialties.length === 1) {
          return tableProvider.specialties[0];
        }
        return null;
      }
      if (prev && workflowsList.some((w) => w.specialty === prev)) return prev;
      return null;
    });
  }, [workflowsList, tableProvider]);

  const selectedProvider = tableProvider?.id || null;
  const selectedProviderName = tableProvider?.name || null;

  const pathForProvider = useCallback(
    (provider, view) =>
      provider
        ? doctorWorkflowsPath(provider.id, { view })
        : workflowsViewPath(view),
    []
  );

  // Flipping views swaps one path segment and keeps the doctor.
  const handleViewModeChange = useCallback(
    (next) => {
      navigate(pathForProvider(tableProvider, next));
    },
    [navigate, pathForProvider, tableProvider]
  );

  // Selecting a doctor drops the table specialty pick so they start fresh.
  const handleTableProviderChange = useCallback(
    (provider) => {
      setTableSpecialtyPick(null);
      navigate(pathForProvider(provider, TABLE_VIEW));
    },
    [navigate, pathForProvider]
  );

  const handleTableSpecialtyChange = useCallback((specialty) => {
    setTableSpecialtyPick(specialty);
  }, []);

  const openSpecialtySearch = useCallback(() => {
    setSpecialtyOpen(true);
    setSpecialtySearch("");
    specialtyInputRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    // Without this, a backend that never responds leaves the page loading
    // forever.
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const response = await customFetch(
          `${environment.apiUrl}/workflows/load-from-s3`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        const result = await response.json();
        if (cancelled) return;
        const list = (result?.success && result?.data?.workflows) || [];
        setWorkflowsList(list);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err?.name === "AbortError"
              ? "Timed out loading workflows."
              : "Failed to load workflows."
          );
        }
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!specialtyOpen) return undefined;
    const onDown = (e) => {
      if (specialtyRef.current && !specialtyRef.current.contains(e.target)) {
        setSpecialtyOpen(false);
        setSpecialtySearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [specialtyOpen]);

  useEffect(() => {
    if (!flagsOpen) return undefined;
    const onDown = (e) => {
      if (flagsRef.current && !flagsRef.current.contains(e.target)) {
        setFlagsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [flagsOpen]);

  const workflow = useMemo(
    () => workflowsList.find((w) => w.specialty === selectedSpecialty) || null,
    [selectedSpecialty, workflowsList]
  );

  // The specialty-local spelling of this id, since production compares
  // normalized display names rather than ids.
  const matchProviderName = useMemo(
    () => resolveProviderName(workflow, selectedProvider, selectedProviderName),
    [workflow, selectedProvider, selectedProviderName]
  );

  const filteredSpecialties = useMemo(() => {
    const query = specialtySearch.trim().toLowerCase();
    if (!query) return workflowsList;
    return workflowsList.filter((w) => w.specialty.toLowerCase().includes(query));
  }, [specialtySearch, workflowsList]);

  const providers = useMemo(
    () => (workflow ? collectProviders(workflow) : []),
    [workflow]
  );

  const visitTypes = useMemo(
    () => (workflow ? collectVisitTypes(workflow) : []),
    [workflow]
  );

  const flags = useMemo(() => (workflow ? collectFlags(workflow) : []), [workflow]);

  const expandedKeys = useMemo(
    () => expandedBySpecialty[selectedSpecialty] || new Set(),
    [expandedBySpecialty, selectedSpecialty]
  );

  const collapsedKeys = useMemo(
    () => collapsedBySpecialty[selectedSpecialty] || new Set(),
    [collapsedBySpecialty, selectedSpecialty]
  );

  const conditionsActive = Boolean(selectedVisitType) || selectedFlags.size > 0;

  // The winning route, by the backend's first-match rule. No visit type and no
  // flags means no visit described yet, so we deliberately match nothing.
  const matched = useMemo(() => {
    if (!workflow || !matchProviderName) return null;
    if (selectedVisitType === null && selectedFlags.size === 0) return null;
    const patientType =
      selectedVisitType === ANY_VISIT_TYPE ? null : selectedVisitType;
    return matchRoute(workflow, {
      providerName: matchProviderName,
      patientType,
      flags: [...selectedFlags],
    });
  }, [workflow, matchProviderName, selectedVisitType, selectedFlags]);

  const matchedRouteIndex = matched && !matched.isDefault ? matched.index : null;

  useEffect(() => {
    setTraceOpen(false);
  }, [selectedSpecialty, selectedProvider, selectedVisitType, selectedFlags]);

  // Step-by-step reasoning behind the match, for the "why this route?" trace.
  const matchTrace = useMemo(() => {
    if (!matched || !workflow) return null;
    return explainMatch(workflow, {
      providerName: matchProviderName,
      patientType:
        selectedVisitType === ANY_VISIT_TYPE ? null : selectedVisitType,
      flags: [...selectedFlags],
    });
  }, [matched, workflow, matchProviderName, selectedVisitType, selectedFlags]);

  // Blocks to emphasize: the matched route's, or those the conditions touch.
  const highlightBlockKeys = useMemo(() => {
    if (!workflow) return new Set();
    if (matched && !matched.isDefault) {
      return new Set([blockKeyForRoute(matched.route)]);
    }
    const keys = new Set();
    // "Any" emphasizes the any-visit-type branch even without a provider.
    if (selectedVisitType === ANY_VISIT_TYPE) keys.add("any");
    if (conditionsActive) {
      for (const route of workflow.routes || []) {
        const types = route?.conditions?.patient_types || [];
        const routeFlags = route?.conditions?.flags || [];
        const typeHit =
          selectedVisitType &&
          selectedVisitType !== ANY_VISIT_TYPE &&
          types.includes(selectedVisitType);
        const flagHit = routeFlags.some((f) => selectedFlags.has(f));
        if (typeHit || flagHit) keys.add(blockKeyForRoute(route));
      }
    }
    return keys;
  }, [workflow, matched, conditionsActive, selectedVisitType, selectedFlags]);

  // Where the canvas should zoom: matched route's block > relevant condition
  // block > default node (when nothing matched) > manual toggle.
  const focusBlockKey = useMemo(() => {
    if (matched && !matched.isDefault) return blockKeyForRoute(matched.route);
    // "Any" zooms to that section even when the match falls through to the
    // default template.
    if (selectedVisitType === ANY_VISIT_TYPE) return "any";
    if (matched && matched.isDefault) return "default-route";
    if (conditionsActive) return [...highlightBlockKeys][0] || null;
    return manualFocusKey;
  }, [matched, selectedVisitType, conditionsActive, highlightBlockKeys, manualFocusKey]);

  const fitSignature = `${selectedSpecialty}|${matchProviderName || ""}|${
    selectedVisitType || ""
  }|${[...selectedFlags].sort().join(",")}`;

  const resetSelections = () => {
    navigate(workflowsViewPath(TREE_VIEW));
    setSelectedVisitType(null);
    setSelectedFlags(new Set());
    setManualFocusKey(null);
    setExpandedBySpecialty((prev) => ({ ...prev, [selectedSpecialty]: new Set() }));
    setCollapsedBySpecialty((prev) => ({ ...prev, [selectedSpecialty]: new Set() }));
  };

  // A doctor belongs to the specialty you picked them from, so changing it
  // clears them rather than carrying them somewhere they don't work.
  const handleSelectSpecialty = useCallback(
    (specialty) => {
      setSelectedSpecialty(specialty);
      setSpecialtyOpen(false);
      setSpecialtySearch("");
      setSelectedVisitType(null);
      setSelectedFlags(new Set());
      setManualFocusKey(null);
      // Expand/collapse state is not carried over between specialties.
      setExpandedBySpecialty({});
      setCollapsedBySpecialty({});
      if (tableProvider && !tableProvider.specialties.includes(specialty)) {
        navigate(workflowsViewPath(TREE_VIEW));
      }
    },
    [navigate, tableProvider]
  );

  // Manual overrides go stale on a new selection: a hand-collapsed block has to
  // reopen when a provider auto-expands it.
  const clearManualOverrides = useCallback(() => {
    setExpandedBySpecialty((prev) => ({ ...prev, [selectedSpecialty]: new Set() }));
    setCollapsedBySpecialty((prev) => ({ ...prev, [selectedSpecialty]: new Set() }));
  }, [selectedSpecialty]);

  // Takes the dropdown's provider id.
  const handleSelectProvider = useCallback(
    (providerId) => {
      const provider = allProviders.find((p) => p.id === providerId) || null;
      navigate(pathForProvider(provider, TREE_VIEW));
      setManualFocusKey(null);
      clearManualOverrides();
    },
    [allProviders, navigate, pathForProvider, clearManualOverrides]
  );

  const toggleFlag = useCallback(
    (flag) => {
      setSelectedFlags((prev) => {
        const next = new Set(prev);
        if (next.has(flag)) next.delete(flag);
        else next.add(flag);
        return next;
      });
      setManualFocusKey(null);
      clearManualOverrides();
    },
    [clearManualOverrides]
  );

  // Effective expanded state for a block, mirroring buildGraph's logic.
  const isBlockExpanded = useCallback(
    (blockKey) => {
      const auto = Boolean(matchProviderName) || highlightBlockKeys.has(blockKey);
      const expanded = (expandedKeys.has(blockKey) || auto) && !collapsedKeys.has(blockKey);
      return expanded;
    },
    [matchProviderName, highlightBlockKeys, expandedKeys, collapsedKeys]
  );

  const setBlockState = useCallback(
    (blockKey, expand) => {
      setExpandedBySpecialty((prev) => {
        const next = new Set(prev[selectedSpecialty] || []);
        if (expand) next.add(blockKey);
        else next.delete(blockKey);
        return { ...prev, [selectedSpecialty]: next };
      });
      setCollapsedBySpecialty((prev) => {
        const next = new Set(prev[selectedSpecialty] || []);
        if (expand) next.delete(blockKey);
        else next.add(blockKey);
        return { ...prev, [selectedSpecialty]: next };
      });
    },
    [selectedSpecialty]
  );

  // Clicking a block expands it and zooms into the branch; clicking again
  // collapses it and returns to the overview.
  const handleToggleBlock = useCallback(
    (blockKey) => {
      const willExpand = !isBlockExpanded(blockKey);
      setBlockState(blockKey, willExpand);
      setManualFocusKey(willExpand ? blockKey : null);
    },
    [isBlockExpanded, setBlockState]
  );

  // Guessing which view an unrecognized segment meant is worse than asking.
  if (unknownView) {
    return <Navigate to={workflowsChooserPath()} replace />;
  }

  return (
    <div className="w-screen h-screen">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div
        className="flex h-screen flex-col px-8 pt-8 pb-6"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Workflows Visualization
            </h1>
            {/* The chooser explains itself, so the header stays quiet there. */}
            {viewMode && (
              <p className="text-sm text-gray-500">
                {viewMode === TABLE_VIEW
                  ? "Every route that applies to one doctor, in the order the backend evaluates them."
                  : "Visualization of workflow routes. Pick a provider, visit type, and flags if present to see which route is selected."}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* With only two views, naming the other one says where you'll land
                and, by implication, where you are. The sidebar leads back to
                the chooser. */}
            {viewMode && (
              <button
                type="button"
                onClick={() =>
                  handleViewModeChange(
                    viewMode === TABLE_VIEW ? TREE_VIEW : TABLE_VIEW
                  )
                }
                className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-ktx-purple hover:text-ktx-purple"
              >
                <ArrowLeftRight className="h-4 w-4" />
                {viewMode === TABLE_VIEW ? "Switch to Flowchart" : "Switch to Table"}
              </button>
            )}
            <Badge className="flex items-center gap-1.5 border-gray-300 bg-gray-100 text-gray-600">
              <Eye className="h-3.5 w-3.5" />
              Read-only
            </Badge>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {!viewMode ? (
            <ViewChooser />
          ) : viewMode === TABLE_VIEW ? (
            <TableView
              workflowsList={workflowsList}
              loading={loading}
              loadError={loadError}
              selectedProviderId={tableProvider?.id || null}
              selectedSpecialty={tableSpecialty}
              onSelectProvider={handleTableProviderChange}
              onSelectSpecialty={handleTableSpecialtyChange}
              doctorId={doctorId}
            />
          ) : (
          /* Canvas + condition controls */
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              {/* Specialty search-and-select */}
              <div className="flex items-center gap-1.5">
                <label className="text-sm font-medium text-gray-700">Specialty:</label>
                <div className="relative" ref={specialtyRef}>
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={specialtyInputRef}
                    className={`h-9 w-56 rounded-md border bg-white pl-8 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-ktx-purple ${
                      selectedSpecialty
                        ? "border-gray-300"
                        : "border-ktx-purple ring-2 ring-ktx-purple/20"
                    }`}
                    placeholder="Search specialty..."
                    value={specialtyOpen ? specialtySearch : selectedSpecialty || ""}
                    onFocus={() => {
                      setSpecialtyOpen(true);
                      setSpecialtySearch("");
                    }}
                    onChange={(e) => {
                      setSpecialtyOpen(true);
                      setSpecialtySearch(e.target.value);
                    }}
                  />
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  {specialtyOpen && (
                    <div className="absolute left-0 top-full z-30 mt-1 w-56 rounded-md border border-gray-200 bg-white shadow-lg">
                      <div className="max-h-72 space-y-0.5 overflow-y-auto p-1">
                        {filteredSpecialties.map((w) => (
                          <button
                            key={w.specialty}
                            type="button"
                            onClick={() => handleSelectSpecialty(w.specialty)}
                            className={`block w-full truncate rounded-md px-2.5 py-1.5 text-left text-sm transition-colors ${
                              w.specialty === selectedSpecialty
                                ? "bg-ktx-purple font-semibold text-white"
                                : "text-gray-700 hover:bg-purple-50"
                            }`}
                          >
                            {w.specialty}
                          </button>
                        ))}
                        {filteredSpecialties.length === 0 && (
                          <p className="px-2 py-4 text-center text-sm text-gray-400">
                            No specialties match.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedSpecialty && (
              <>
              <div className="flex items-center gap-1.5">
                <label className="text-sm font-medium text-gray-700">Provider:</label>
                <select
                  className="h-9 w-48 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple"
                  value={selectedProvider || ""}
                  onChange={(e) => handleSelectProvider(e.target.value || null)}
                >
                  <option value="">All providers</option>
                  {providers.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1.5">
                <label className="text-sm font-medium text-gray-700">Visit type:</label>
                <select
                  className={`h-9 w-44 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple ${
                    selectedVisitType ? "text-gray-900" : "text-gray-400"
                  }`}
                  value={selectedVisitType || ""}
                  onChange={(e) => {
                    setSelectedVisitType(e.target.value || null);
                    setManualFocusKey(null);
                    clearManualOverrides();
                  }}
                >
                  <option value="" className="text-gray-400">
                    None selected
                  </option>
                  <option value={ANY_VISIT_TYPE} className="text-gray-900">
                    Any visit type
                  </option>
                  {visitTypes.map((type) => (
                    <option key={type} value={type} className="text-gray-900">
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {flags.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Flags:</label>
                  <div className="relative" ref={flagsRef}>
                    <button
                      type="button"
                      onClick={() => setFlagsOpen((o) => !o)}
                      className="flex h-9 w-48 items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple"
                    >
                      <span className="flex items-center gap-1.5 truncate">
                        <Flag className="h-3.5 w-3.5 shrink-0 text-orange-500" />
                        <span className={selectedFlags.size ? "text-gray-900" : "text-gray-400"}>
                          {selectedFlags.size
                            ? `${selectedFlags.size} selected`
                            : "None selected"}
                        </span>
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                    {flagsOpen && (
                      <div className="absolute left-0 top-full z-30 mt-1 w-56 rounded-md border border-gray-200 bg-white shadow-lg">
                        <div className="max-h-72 space-y-0.5 overflow-y-auto p-1">
                          {flags.map((flag) => {
                            const on = selectedFlags.has(flag);
                            return (
                              <button
                                key={flag}
                                type="button"
                                onClick={() => toggleFlag(flag)}
                                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors ${
                                  on
                                    ? "bg-orange-50 font-semibold text-orange-800"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                    on
                                      ? "border-orange-500 bg-orange-500 text-white"
                                      : "border-gray-300 bg-white"
                                  }`}
                                >
                                  {on && (
                                    <svg
                                      viewBox="0 0 12 12"
                                      className="h-3 w-3"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  )}
                                </span>
                                <span className="truncate">{flag}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(selectedProvider || conditionsActive) && (
                <button
                  type="button"
                  onClick={resetSelections}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                >
                  <X className="h-3 w-3" />
                  Clear
                </button>
              )}
              </>
              )}
            </div>

            {!selectedSpecialty && !loading && !loadError ? (
              <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-6 py-16 text-center">
                <Layers className="h-8 w-8 text-gray-300" />
                <p className="mt-3 text-sm font-semibold text-gray-700">
                  Select a specialty to see its routes
                </p>
                <p className="mt-1 max-w-md text-sm text-gray-500">
                  Pick a specialty to walk its routes as a flowchart and see which
                  template a visit lands on.
                </p>
                <button
                  type="button"
                  onClick={openSpecialtySearch}
                  className="mt-5 cursor-pointer rounded-md bg-ktx-purple px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ktx-purple/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ktx-purple focus-visible:ring-offset-2"
                >
                  Search specialties
                </button>
              </div>
            ) : (
            <>
            {/* Matched route: resource links plus the why-this-route trace. */}
            {matched && (
              <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5">
                <div className="space-y-1">
                  {matched.resources.length === 0 ? (
                    <span className="text-sm italic text-gray-500">
                      No resources on this route.
                    </span>
                  ) : (
                    matched.resources.map((res) => (
                      <div
                        key={res.key}
                        className="flex min-w-0 items-start gap-2"
                      >
                        <span className="mt-px shrink-0 rounded border border-emerald-200 bg-white px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
                          {res.label}
                        </span>
                        <Link
                          to={templatePath(res.id)}
                          className="min-w-0 break-words text-sm text-ktx-purple underline decoration-ktx-purple/40 underline-offset-2 transition-colors hover:decoration-ktx-purple"
                          title={`Find ${res.id} in Templates`}
                        >
                          {res.id}
                        </Link>
                      </div>
                    ))
                  )}
                </div>

                {matchTrace && (
                  <div className="mt-2 border-t border-emerald-200 pt-2">
                    <button
                      type="button"
                      onClick={() => setTraceOpen((o) => !o)}
                      className="flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:text-emerald-900"
                    >
                      {traceOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      Why this route?
                    </button>
                    {traceOpen && (
                      <ol className="mt-1.5 space-y-1">
                        {matchTrace.steps.map((step) => (
                          <li
                            key={step.index}
                            className="flex items-start gap-2 text-sm"
                          >
                            <span
                              className={`mt-0.5 shrink-0 rounded px-1.5 py-0 text-xs font-bold ${
                                step.status === "matched"
                                  ? "bg-emerald-200 text-emerald-900"
                                  : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              Path {step.index + 1}
                            </span>
                            {step.status === "matched" ? (
                              <span className="text-emerald-800">
                                Matched — all conditions satisfied.
                              </span>
                            ) : (
                              <span className="text-gray-600">
                                Skipped — {step.reasons.join("; ")}
                              </span>
                            )}
                          </li>
                        ))}
                        {matchTrace.winner === null && (
                          <li className="flex items-start gap-2 text-sm">
                            <span className="mt-0.5 shrink-0 rounded bg-indigo-200 px-1.5 py-0 text-xs font-bold text-indigo-900">
                              FALLBACK
                            </span>
                            <span className="text-indigo-800">
                              No route matched — fell through to the fallback.
                            </span>
                          </li>
                        )}
                        {matchTrace.otherProviderCount > 0 && (
                          <li className="pl-1 text-xs italic text-gray-400">
                            {matchTrace.otherProviderCount} route
                            {matchTrace.otherProviderCount > 1 ? "s" : ""} for other
                            providers not shown.
                          </li>
                        )}
                      </ol>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              <div className="pointer-events-none absolute right-2 top-2 z-10 rounded-md border border-gray-200 bg-white/95 px-2 py-1.5 shadow-sm">
                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                  Legend
                </div>
                <ResourceLegend vertical />
              </div>
              {loading ? (
                <div className="flex h-full items-center justify-center text-gray-400">
                  Loading workflows...
                </div>
              ) : loadError ? (
                <div className="flex h-full items-center justify-center text-red-500">
                  {loadError}
                </div>
              ) : workflow ? (
                <ReactFlowProvider>
                  <FlowCanvas
                    workflow={workflow}
                    expandedKeys={expandedKeys}
                    collapsedKeys={collapsedKeys}
                    selectedProviderName={matchProviderName}
                    highlightBlockKeys={highlightBlockKeys}
                    matchedRouteIndex={matchedRouteIndex}
                    focusBlockKey={focusBlockKey}
                    fitSignature={fitSignature}
                    onToggleBlock={handleToggleBlock}
                  />
                </ReactFlowProvider>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-400">
                  {workflowsList.length === 0
                    ? "No workflows available."
                    : "Select a specialty to visualize its routes."}
                </div>
              )}
            </div>
            </>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowsVisualization;
